import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { PriceDisplay } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Spinner } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import { List } from '@heartinternet/hi-ui';
import { ListItem } from '@heartinternet/hi-ui';
import type { Product } from '../types';

export interface DomainMailboxes {
  domain: string;
  mailbox: string[];
  package_oid: string;
}

interface PremiumMailboxCardProps {
  product: Product;
  formAction: string;
  isReseller?: boolean;
  fetchMailboxes: () => Promise<DomainMailboxes[]>;
}

export const PremiumMailboxCard: React.FC<PremiumMailboxCardProps> = ({
  product,
  formAction,
  isReseller,
  fetchMailboxes,
}) => {
  const [loading, setLoading] = useState(true);
  const [domainMailboxes, setDomainMailboxes] = useState<DomainMailboxes[]>([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedMailbox, setSelectedMailbox] = useState('');

  const price =
    isReseller && product.price!.resellerAmount != null
      ? product.price!.resellerAmount
      : product.price!.amount;

  const loadMailboxes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMailboxes();
      setDomainMailboxes(data);
      if (data.length > 0) {
        setSelectedDomain(data[0].domain);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchMailboxes]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: fetch mailbox data when the memoised loader changes (component mount + domain switch)
    loadMailboxes();
  }, [loadMailboxes]);

  const currentDomainData = domainMailboxes.find((d) => d.domain === selectedDomain);
  const mailboxes = currentDomainData?.mailbox || [];

  const handleDomainChange = (domain: string) => {
    setSelectedDomain(domain);
    setSelectedMailbox('');
  };

  const domainIndex = domainMailboxes.findIndex((d) => d.domain === selectedDomain) + 1;
  const isValid = selectedMailbox.length > 0;

  return (
    <div className="hi-ui-extras-page__product-content">
      <Text variant="body" inline>
        {product.description}
      </Text>
      <div className="hi-ui-extras-page__specs-grid">
        <div className="hi-ui-extras-page__specs-card">
          <List className="hi-ui-extras-page__features hi-ui-extras-page__features--icons">
            {product.features.map((text, i) => (
              <ListItem key={i}>
                <Icon name="check-circle-grey" size={18} />
                {text}
              </ListItem>
            ))}
          </List>
        </div>
      </div>
      {loading ? (
        <div className="hi-ui-extras-page__loading">
          <Spinner size="medium" />
        </div>
      ) : domainMailboxes.length === 0 ? (
        <Text variant="body">No mailboxes found to upgrade.</Text>
      ) : (
        <Form action={formAction} method="post">
          <input type="hidden" name="type" value="MULTI" />
          <input type="hidden" name="submit-type" value="PURCHASE" />
          <input type="hidden" name="basket-add-code" value={product.basketAddCode} />
          <input type="hidden" name="count" value={domainIndex} />

          <Card variant="surface" padding="md" className="hi-ui-extras-page__card-form">
            <FormField label="Domain" fullWidth>
              <Select
                value={selectedDomain}
                onChange={(e) => handleDomainChange(e.target.value)}
                fullWidth
              >
                {domainMailboxes.map((d) => (
                  <option key={d.domain} value={d.domain}>
                    {d.domain}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField label="Mailbox to upgrade" fullWidth>
              <Select
                name={`domain_mailbox${domainIndex}`}
                value={selectedMailbox}
                onChange={(e) => setSelectedMailbox(e.target.value)}
                fullWidth
              >
                <option value="">(Please select)</option>
                {mailboxes.map((mb) => (
                  <option key={mb} value={`2GB_upgrade:${mb}`}>
                    {mb}
                  </option>
                ))}
              </Select>
            </FormField>

            <div className="hi-ui-extras-page__card-footer">
              <PriceDisplay
                amount={price}
                currency={product.price!.currency}
                interval={product.price!.interval}
                intervalFormat="long"
                size="lg"
              />
              <Button type="submit" variant="primary" disabled={!isValid}>
                Add to basket
              </Button>
            </div>
          </Card>
        </Form>
      )}
    </div>
  );
};
